import { type PayloadAction, createSlice } from '@reduxjs/toolkit'

import appConfig from '../../../app.config';

import { WebId, type Event, type ActionSetChannelData, type ActionSetStationId, DefaultChannelVolume, Song } from './orchestrator';
import { Cube as CubePlayer, Web } from '../../players';
import { transformTmsCubeDataToChannels } from '../../helpers/transform-tms-cube-data';
import { t } from 'i18next';

export interface Channel {
  channel: number
  volume: number
  name?: string
  stationId?: number
  jingleId?: number
  jingleForce?: boolean
  cubeEvents?: Event[]
}

export interface Player {
  id: number
  type: string
  name: string
  isOnline: boolean
  channels: Record<number, Channel>
  nChannels: number,
  allowVolumeChange: boolean
}

export interface Cube extends Player {
  model: string
  serial: string
  cubeImage: string
  allowVolumeChange: boolean
  iceCastUrl?: string
}

export interface Jingle extends Song {
  jingleId: number
  name: string
  volume: number
  asStation: boolean
  playAt: string[]
}

export interface Station {
  id: number
  subscribedViaProductId: number
  name: string
  displayName: string
  descriptionPublic: string
  images: any
  thumbnailUri: string | null
  stationType: string
  productStationType: string
  showTimetable: boolean
}

export interface UserState {
  loggedIn: boolean
  customerData?: any
  players: Record<number, Player>
  jingles: Jingle[]
  stations: Station[]
  language: string | null
}

interface ActionLoginPayload {}

type ActionLogin = PayloadAction<ActionLoginPayload>;

export interface ActionSetLanguagePayload {
  language: string
}

type ActionSetLanguage = PayloadAction<ActionSetLanguagePayload>;

const initialState: UserState = {
  loggedIn: false,
  players: {},
  jingles: [],
  stations: [],
  language: null
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    login (state, { payload }: ActionLogin) {
      state.loggedIn = true;
      state.customerData = payload;

      // Jingles
      state.jingles = [];

      (appConfig.features.jingles ? state.customerData.Jingles : [])
      .filter((e: any) => !!parseInt(e.Jingle.active))
      .filter((e: any) => e.Jingle.validity_start === null || new Date() >= new Date(e.Jingle.validity_start))
      .filter((e: any) => e.Jingle.validity_end === null || new Date() <= new Date(e.Jingle.validity_end))
      .filter((e: any) => !!parseInt(e.Jingle.as_station) || e.Jingle.recurring)
      .forEach((e: any) => {
        const { Jingle: j, Song: s } = e;
        const jingle: Jingle = {
          jingleId: parseInt(j.id),
          name: j.display_name,
          artist: s.artist,
          duration: s.total_time / 1000.0,
          persistentId: s.persistent_id,
          songId: parseInt(s.id),
          volume: j.volume,
          genre: s.genre,
          title: s.name,
          asStation: !!parseInt(e.Jingle.as_station),
          playAt: ((j.recurring || '') as string).split(',').filter(e => e.trim())
        };

        state.jingles.push(jingle);
      });

      state.jingles.sort((a, b) => a.jingleId - b.jingleId);

      // Players
      state.players = {
        [WebId]: {
          type: Web,
          id: WebId,
          name: t('navbar.web_player'),
          channels: {
            1: {
              channel: 1,
              volume: DefaultChannelVolume
            }
          },
          nChannels: 1,
          isOnline: true,
          allowVolumeChange: true
        }
      };

      (appConfig.features.cubes ? state.customerData.Cubes : []).forEach((cube: any) => {
        const player: Cube = {
          type: CubePlayer,
          id: typeof cube.id === 'string' ? parseInt(cube.id as string) : ~~cube.id,
          name: cube.name,
          channels: transformTmsCubeDataToChannels({ config: cube }),
          model: cube.hw_model,
          nChannels: cube.hw_model.startsWith('KCC') || cube.hw_model.startsWith('TCV') ? 1 : 4,
          serial: cube.serial_no,
          cubeImage: cube.cube_image,
          isOnline: !!cube.is_online,
          allowVolumeChange: !!parseInt(cube.allow_volume_change),
          iceCastUrl: cube?.icecast_url
        };

        state.players[cube.id] = player;
      });

      // Stations
      let stations: Record<number, Station> = {};

      state.customerData?.FlatSubscribedProducts
      .filter((product: any) => product.station_id !== null)
      .forEach((product: any) => {
        const station: Station = {
          id: parseInt(product.station_id),
          subscribedViaProductId: parseInt(product.id),
          name: product.name,
          displayName: product.display_name,
          descriptionPublic: product.description_public,
          images: product.images,
          thumbnailUri: product.station_thumbnail_uri || null,
          stationType: product.station_type,
          productStationType: product.product_station_type,
          showTimetable: Boolean(parseInt(product.display_timetable_webui))
        };

        stations[station.id] = station;
      });

      state.stations = Object.values(stations);
      state.stations.sort((a, b) => a.id - b.id);
    },

    logout (state) {
      state.loggedIn = false;
      delete state.customerData;
    },

    setChannelData (state, { payload: { data } }: ActionSetChannelData) {
      Object.values(data).forEach(player => {
        const { playerId, isOnline, channels } = player;

        if(state.players[playerId] === undefined) {
          return;
        }

        state.players[playerId].isOnline = isOnline;

        Object.values(channels).forEach(channelData => {
          const { channel } = channelData;

          state.players[playerId].channels[channel] = {
            ...state.players[playerId].channels[channel],
            ...channelData
          };
        });
      });
    },

    setStationId (state, { payload: { channel, playerId, stationId } }: ActionSetStationId) {
      state.players[playerId].channels[channel].stationId = (
        state.players[playerId].channels[channel].stationId === stationId
          ? undefined
          : stationId
      );
    },

    setLanguage (state, { payload: { language } }: ActionSetLanguage) {
      state.language = language;
    }
  }
});

export const {
  login,
  logout,
  setChannelData,
  setStationId,
  setLanguage
} = userSlice.actions;
export default userSlice.reducer;
